import { z } from "zod";
import type { Operation } from "../types";

export interface FavoriteRow {
  href: string;
  label: string;
  created_at: string;
}

const favoriteRowSchema = z.object({
  href: z.string(),
  label: z.string(),
  created_at: z.string(),
});

const listResponse = z.object({
  favorites: z.array(favoriteRowSchema),
});
type ListResponse = z.infer<typeof listResponse>;

const toggleBody = z.object({
  href: z.string().min(1),
  label: z.string().min(1),
});
type ToggleBody = z.infer<typeof toggleBody>;

// The outer envelope already carries `ok`; the toggle response payload itself
// only needs `favorited`.
const toggleResponse = z.object({
  favorited: z.boolean(),
});
type ToggleResponse = z.infer<typeof toggleResponse>;

const listOp: Operation<void, void, void, ListResponse> = {
  method: "GET",
  path: () => "/api/favorites",
  response: listResponse,
};

const toggleOp: Operation<void, void, ToggleBody, ToggleResponse> = {
  method: "POST",
  path: () => "/api/favorites",
  body: toggleBody,
  response: toggleResponse,
  // RO-5-01 demo: no path params on `listOp`, so a bare reference
  // invalidates its whole family (there's only the one variant here, but
  // this is the same "collection, no per-resource scope" shape every
  // unfiltered list uses). Replaces the `mutate(apiKey(favorites.list, {}))`
  // call `favorite-toggle.tsx` used to make by hand — which, under this
  // shell's custom SWR cache provider, was a silent no-op (see client.ts).
  invalidates: [listOp],
};

export const favorites = {
  list: listOp,
  toggle: toggleOp,
};
